import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Card, Field, Input, Link, MessageBar, MessageBarBody, Spinner, Subtitle2, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { api } from '../../api/client';
import {
  NEEDS_CUSTOMER, STEP_LABELS, STEP_ORDER, type DeploymentSession, type ProductManifest, type StepId, type StepState,
} from '../../api/types';
import { connectCustomer, currentCustomer, customerTokens, type CustomerConnection } from '../../customer/customerAuth';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center', flexWrap: 'wrap' },
  step: { display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: tokens.spacingHorizontalM, alignItems: 'start',
          padding: `${tokens.spacingVerticalS} 0`, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  num: { color: tokens.colorNeutralForeground3, fontWeight: 600 },
  msg: { color: tokens.colorNeutralForeground2, display: 'block' },
});

const badge: Record<StepState['status'], 'informative' | 'brand' | 'success' | 'danger' | 'subtle'> = {
  pending: 'subtle', running: 'brand', done: 'success', failed: 'danger', skipped: 'informative',
};

/** First step that is not done/skipped — the only one that may run next. */
function nextStep(s: DeploymentSession): StepId | undefined {
  return STEP_ORDER.find((id) => !['done', 'skipped'].includes(s.steps[id].status));
}

export function SessionView({ id }: { id: string }) {
  const styles = useStyles();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery({ queryKey: ['session', id], queryFn: () => api<DeploymentSession>(`sessions/${id}`) });
  const products = useQuery({ queryKey: ['products'], queryFn: () => api<ProductManifest[]>('products') });
  const manifest = products.data?.find((p) => p.productId === session.data?.productId);

  const [customer, setCustomer] = useState<CustomerConnection | null>(currentCustomer());
  const [connectError, setConnectError] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [outputs, setOutputs] = useState<Record<string, string>>({});

  const run = useMutation({
    mutationFn: async ({ step, input }: { step: StepId; input?: Record<string, unknown> }) =>
      api<DeploymentSession>(`sessions/${id}/steps/${step}`, {
        body: input ?? {},
        customer: NEEDS_CUSTOMER.includes(step) ? await customerTokens() : undefined,
      }),
    onSuccess: (s) => qc.setQueryData(['session', id], s),
    // The API records failures in the session before returning the error — reload to show them.
    onError: () => qc.invalidateQueries({ queryKey: ['session', id] }),
  });

  const updateSite = useMutation({
    mutationFn: (url: string) => api<DeploymentSession>(`sessions/${id}/site`, { body: { siteUrl: url } }),
    onSuccess: (s) => { qc.setQueryData(['session', id], s); setSiteUrl(''); },
  });

  const abandon = useMutation({
    mutationFn: () => api<DeploymentSession>(`sessions/${id}/abandon`, { method: 'POST', body: {} }),
    onSuccess: () => navigate('/deploy'),
  });

  if (session.isLoading) return <Spinner label="Loading deployment…" />;
  if (session.error) return <MessageBar intent="error"><MessageBarBody>{(session.error as Error).message}</MessageBarBody></MessageBar>;
  const s = session.data!;
  const next = nextStep(s);
  const busy = run.isPending;

  async function connect() {
    setConnectError(null);
    try {
      const c = await connectCustomer();
      if (c.tenantId !== s.tenantId) {
        setConnectError(`That account belongs to tenant ${c.tenantId}; this licence is for ${s.tenantId}. Sign in with an admin of the licensed tenant.`);
        return;
      }
      setCustomer(c);
    } catch (e) {
      setConnectError((e as Error).message);
    }
  }

  function runNext() {
    if (!next) return;
    if (next === 'azure') {
      run.mutate({ step: 'azure', input: { outputs } });
      return;
    }
    run.mutate({ step: next });
  }

  const needsCustomer = next && NEEDS_CUSTOMER.includes(next) && !customer;

  return (
    <div className={styles.stack}>
      <Card>
        <div className={styles.row}>
          <Subtitle2>{s.displayName} {s.version}</Subtitle2>
          <Badge appearance="tint" color={s.status === 'completed' ? 'success' : s.status === 'abandoned' ? 'subtle' : 'brand'}>{s.status}</Badge>
        </div>
        <Text>Customer: {s.customerName || '—'} · Tenant {s.tenantId}</Text>
        <Text>Site: {s.siteUrl ? <Link href={s.siteUrl} target="_blank" rel="noreferrer">{s.siteUrl}</Link> : 'set after the admin signs in'}</Text>
        <Text>Started by {s.createdBy.name} · {new Date(s.createdAt).toLocaleString()}</Text>
      </Card>

      <Card>
        <Subtitle2>Customer Global Admin</Subtitle2>
        {customer
          ? <Text>Signed in as <b>{customer.username}</b> ({customer.sharePointHost}). Their sign-in stays in this tab only.</Text>
          : <Text>The customer&apos;s Global Admin signs in here — on this screen, with you. Their account must belong to the licensed tenant.</Text>}
        <div className={styles.row}>
          <Button onClick={connect} disabled={busy}>{customer ? 'Sign in a different admin' : 'Sign in customer admin'}</Button>
        </div>
        {connectError && <MessageBar intent="error"><MessageBarBody>{connectError}</MessageBarBody></MessageBar>}
      </Card>

      {s.steps.connect.status === 'done' && s.steps.site.status !== 'done' && (
        <Card>
          <Subtitle2>SharePoint site</Subtitle2>
          <div className={styles.row}>
            <Field label="Site URL (created if it does not exist)">
              <Input style={{ minWidth: 420 }} value={siteUrl || s.siteUrl} onChange={(_, d) => setSiteUrl(d.value)} />
            </Field>
            <Button disabled={!siteUrl || siteUrl === s.siteUrl || updateSite.isPending} onClick={() => updateSite.mutate(siteUrl)}>Use this site</Button>
          </div>
          {updateSite.error && <MessageBar intent="error"><MessageBarBody>{(updateSite.error as Error).message}</MessageBarBody></MessageBar>}
        </Card>
      )}

      <Card>
        <Subtitle2>Steps</Subtitle2>
        {STEP_ORDER.map((stepId, i) => {
          const st = s.steps[stepId];
          return (
            <div key={stepId} className={styles.step}>
              <span className={styles.num}>{i + 1}</span>
              <div>
                <Text weight="semibold">{STEP_LABELS[stepId]}</Text>
                {st.message && <Text size={200} className={styles.msg}>{st.message}</Text>}
              </div>
              <Badge appearance="filled" color={badge[st.status]}>{busy && next === stepId ? 'running…' : st.status}</Badge>
            </div>
          );
        })}

        {next === 'azure' && manifest && (manifest.azure.required || manifest.azure.optional) && (
          <div className={styles.stack}>
            <Text>
              The customer deploys this product&apos;s Azure backend with the handoff pack
              {manifest.azure.guideUrl && <> (<Link href={manifest.azure.guideUrl} target="_blank" rel="noreferrer">guide</Link>)</>}.
              Enter what their deployment reports, then verify.
            </Text>
            {(manifest.azure.outputs ?? []).map((o) => (
              <Field key={o.key} label={o.label} hint={o.help}>
                <Input value={outputs[o.key] ?? ''} onChange={(_, d) => setOutputs({ ...outputs, [o.key]: d.value })} />
              </Field>
            ))}
            {manifest.azure.optional && (
              <Button disabled={busy} onClick={() => run.mutate({ step: 'azure', input: { skip: true } })}>
                Skip the optional backend for now
              </Button>
            )}
          </div>
        )}

        {run.error && <MessageBar intent="error"><MessageBarBody>{(run.error as Error).message}</MessageBarBody></MessageBar>}

        <div className={styles.row} style={{ marginTop: tokens.spacingVerticalM }}>
          {s.status === 'active' && next && (
            <Button appearance="primary" disabled={busy || !!needsCustomer} onClick={runNext}
                    title={needsCustomer ? 'Sign in the customer admin first' : undefined}>
              {busy ? 'Working…' : s.steps[next].status === 'failed' ? `Retry: ${STEP_LABELS[next]}` : `Run: ${STEP_LABELS[next]}`}
            </Button>
          )}
          {s.status === 'active' && <Button disabled={busy || abandon.isPending} onClick={() => abandon.mutate()}>Abandon</Button>}
          {s.status === 'completed' && (
            <MessageBar intent="success"><MessageBarBody>
              Done. Open <Link href={s.siteUrl} target="_blank" rel="noreferrer">the site</Link> once so the product&apos;s setup wizard starts.
            </MessageBarBody></MessageBar>
          )}
        </div>
      </Card>
    </div>
  );
}
